import { useTranslation } from 'react-i18next';
import { Images, Palette, ScanFace, UsersRound, type LucideIcon } from 'lucide-react';
import { appearanceSlug } from '@/lib/storybook-utils';
import { cn } from '@/lib/utils';
import { PromoImg } from './PromoImg';
import { SamplePhotosTag, SectionHeading } from './SectionHeading';
import { PHOTO_FACE_BOXES, SAMPLE_NAMES, SAMPLE_PHOTOS, type SamplePerson } from './promo';

/** What the builder offers when /config hasn't answered yet (services/storybook/config.js RELATIONS). */
const DEFAULT_RELATIONS = ['mom', 'dad', 'grandma', 'grandpa', 'aunt', 'uncle', 'big sister', 'big brother', 'friend'];

const CHIP_TINTS = [
  'bg-[hsl(330_85%_94%)] text-[hsl(330_55%_32%)]',
  'bg-secondary text-secondary-foreground',
  'bg-[hsl(45_95%_88%)] text-[hsl(30_70%_26%)]',
];

/** The sample family as the photo step would show it once each face is tapped. */
const TAGGED: Array<{ person: SamplePerson; relation?: string }> = [
  { person: 'mia' },
  { person: 'ana', relation: 'mom' },
  { person: 'tom', relation: 'dad' },
];

interface FamilySectionProps {
  /** Relations GET /config offers; null while it hasn't answered. */
  relations: string[] | null;
  maxCompanions: number;
  maxPhotos: number;
}

/** "Bring the family": who can join the story, and how one family photo is tagged. */
export function FamilySection({ relations, maxCompanions, maxPhotos }: FamilySectionProps) {
  const { t } = useTranslation();
  const offered = relations ?? DEFAULT_RELATIONS;
  // The backend takes no companions (yet): the builder shows none either.
  if (offered.length === 0 || maxCompanions < 1) return null;

  const relationLabel = (relation: string) =>
    t(`storybookPage.relations.${appearanceSlug(relation)}`, { defaultValue: relation });

  const points: Array<{ key: string; icon: LucideIcon; text: string }> = [
    { key: 'who', icon: UsersRound, text: t('storybookPage.family.points.who', { count: maxCompanions }) },
    ...(maxPhotos > 0
      ? [
          { key: 'photos', icon: Images, text: t('storybookPage.family.points.photos', { max: maxPhotos }) },
          { key: 'tap', icon: ScanFace, text: t('storybookPage.family.points.tap') },
        ]
      : []),
    { key: 'noPhoto', icon: Palette, text: t('storybookPage.family.points.noPhoto') },
  ];

  return (
    <section id="family" className="scroll-mt-20 py-14 sm:py-20" aria-labelledby="storybook-family-title">
      <div className="container mx-auto grid max-w-5xl items-center gap-10 px-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-14">
        <div>
          <SectionHeading
            id="storybook-family-title"
            align="start"
            className="mb-6"
            eyebrow={t('storybookPage.family.eyebrow')}
            title={t('storybookPage.family.title')}
            subtitle={t('storybookPage.family.subtitle')}
          />
          <ul className="flex flex-wrap justify-center gap-2 lg:justify-start" aria-label={t('storybookPage.family.relationsLabel')}>
            {offered.map((relation, index) => (
              <li
                key={relation}
                className={cn('rounded-full px-3.5 py-1.5 text-sm font-semibold shadow-sm', CHIP_TINTS[index % CHIP_TINTS.length])}
              >
                {relationLabel(relation)}
              </li>
            ))}
          </ul>
          <ul className="mx-auto mt-6 max-w-md space-y-3 lg:mx-0">
            {points.map((point) => (
              <li key={point.key} className="flex items-start gap-3 text-[15px] leading-snug">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-card text-primary shadow-sm ring-1 ring-border">
                  <point.icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <span className="pt-1">{point.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {maxPhotos > 0 && (
          <figure className="glass-card mx-auto w-full max-w-md p-3 sm:p-4 lg:max-w-none">
            <div className="grid grid-cols-3 gap-1 overflow-hidden rounded-2xl">
              {TAGGED.map(({ person, relation }) => {
                const [left, top, right, bottom] = PHOTO_FACE_BOXES[person];
                return (
                  <div key={person} className="relative aspect-square">
                    <PromoImg
                      image={SAMPLE_PHOTOS[person]}
                      sizes="(min-width: 1024px) 160px, (min-width: 480px) 140px, 30vw"
                      className="h-full w-full object-cover"
                    />
                    <span
                      aria-hidden="true"
                      className="absolute rounded-lg border-2 border-primary bg-primary/10"
                      style={{
                        left: `${left * 100}%`,
                        top: `${top * 100}%`,
                        width: `${(right - left) * 100}%`,
                        height: `${(bottom - top) * 100}%`,
                      }}
                    >
                      <span className="absolute left-1/2 top-full mt-1 max-w-[6rem] -translate-x-1/2 truncate whitespace-nowrap rounded-full bg-primary px-2 py-0.5 text-[11px] font-bold leading-tight text-primary-foreground shadow">
                        {relation ? relationLabel(relation) : SAMPLE_NAMES[person]}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
            <figcaption className="mt-3 flex flex-wrap items-center gap-2 text-sm font-semibold">
              <ScanFace className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <span className="min-w-0 flex-1">{t('storybookPage.family.visualCaption')}</span>
              <SamplePhotosTag label={t('storybookPage.promo.samplePhotos')} />
            </figcaption>
          </figure>
        )}
      </div>
    </section>
  );
}
